import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import React, { useCallback, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useContactImport } from "../hooks/useContactImport";
import {
  ImportContact,
  ImportContactStatus,
  ImportPhase,
} from "../types/contactImport.types";

const PAGE_SIZE = 20;

interface ContactImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: string;
  isAdmin: boolean;
  organizationId?: string;
}

const ContactImportModal: React.FC<ContactImportModalProps> = ({
  isOpen,
  onClose,
  role,
  isAdmin,
  organizationId,
}) => {
  const {
    contacts,
    phase,
    isProcessing,
    progress,
    parseFile,
    startImport,
    reset,
    removeContact,
  } = useContactImport({ role, isAdmin, organizationId });

  const [currentPage, setCurrentPage] = useState(1);
  const [dragActive, setDragActive] = useState(false);

  const totalPages = Math.max(1, Math.ceil(contacts.length / PAGE_SIZE));
  const paginatedContacts = useMemo(
    () =>
      contacts.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
      ),
    [contacts, currentPage]
  );

  const handleClose = () => {
    reset();
    setCurrentPage(1);
    onClose();
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.[0]) {
        parseFile(e.dataTransfer.files[0]);
      }
    },
    [parseFile]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.[0]) {
        parseFile(e.target.files[0]);
      }
    },
    [parseFile]
  );

  const handleDownloadTemplate = useCallback(() => {
    const templateData = [
      { email: "john@example.com" },
      { email: "jane@example.com" },
      { email: "bob@example.com" },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "contacts_template.xlsx");
  }, []);

  const pendingCount = contacts.filter(
    (c) => c.status === ImportContactStatus.PENDING
  ).length;
  const successCount = contacts.filter(
    (c) => c.status === ImportContactStatus.SUCCESS
  ).length;
  const errorCount = contacts.filter(
    (c) => c.status === ImportContactStatus.ERROR
  ).length;
  const duplicateCount = contacts.filter(
    (c) => c.status === ImportContactStatus.DUPLICATE
  ).length;

  return (
    <ModalBlank
      id="contact-import-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      showCloseButton={true}
      closeOnOutClick={false}
      className="max-w-2xl"
    >
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Import Contacts
        </h3>

        {phase === ImportPhase.UPLOAD && (
          <UploadPhase
            dragActive={dragActive}
            onDrag={handleDrag}
            onDrop={handleDrop}
            onFileChange={handleFileChange}
            onDownloadTemplate={handleDownloadTemplate}
          />
        )}

        {(phase === ImportPhase.PREVIEW ||
          phase === ImportPhase.IMPORTING ||
          phase === ImportPhase.COMPLETED) && (
            <>
              <ImportSummary
                total={contacts.length}
                pending={pendingCount}
                success={successCount}
                errors={errorCount}
                duplicates={duplicateCount}
                phase={phase}
                progress={progress}
              />

              <ContactsTable
                contacts={paginatedContacts}
                phase={phase}
                onRemove={removeContact}
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}

        <ModalActions
          phase={phase}
          isProcessing={isProcessing}
          pendingCount={pendingCount}
          onClose={handleClose}
          onStartImport={startImport}
          onReset={reset}
        />
      </div>
    </ModalBlank>
  );
};

const UploadPhase: React.FC<{
  dragActive: boolean;
  onDrag: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDownloadTemplate: () => void;
}> = ({ dragActive, onDrag, onDrop, onFileChange, onDownloadTemplate }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>
        Upload an Excel file (.xlsx) with an <strong>email</strong> column.
      </span>
    </div>

    <button
      type="button"
      onClick={onDownloadTemplate}
      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
    >
      Download template file
    </button>

    <div
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
        : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
        }`}
      onDragEnter={onDrag}
      onDragLeave={onDrag}
      onDragOver={onDrag}
      onDrop={onDrop}
    >
      <input
        type="file"
        id="contact-file-upload"
        className="hidden"
        accept=".xlsx,.xls"
        onChange={onFileChange}
      />
      <svg
        className="mx-auto h-10 w-10 text-gray-400 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Drag and drop your file here, or
      </p>
      <label
        htmlFor="contact-file-upload"
        className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
      >
        browse to select a file
      </label>
    </div>
  </div>
);

const ImportSummary: React.FC<{
  total: number;
  pending: number;
  success: number;
  errors: number;
  duplicates: number;
  phase: ImportPhase;
  progress: { processed: number; total: number };
}> = ({ total, pending, success, errors, duplicates, phase, progress }) => (
  <div className="mb-4 space-y-3">
    <div className="flex flex-wrap gap-3 text-sm">
      <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">
        Total: {total}
      </span>
      {pending > 0 && (
        <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
          Pending: {pending}
        </span>
      )}
      {success > 0 && (
        <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">
          Success: {success}
        </span>
      )}
      {errors > 0 && (
        <span className="px-2 py-1 rounded-full bg-red-100 text-red-800">
          Errors: {errors}
        </span>
      )}
      {duplicates > 0 && (
        <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800">
          Duplicates: {duplicates}
        </span>
      )}
    </div>

    {phase === ImportPhase.IMPORTING && progress.total > 0 && (
      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Processing...</span>
          <span>
            {progress.processed} / {progress.total}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${(progress.processed / progress.total) * 100}%`,
            }}
          />
        </div>
      </div>
    )}
  </div>
);

const StatusBadge: React.FC<{ status: ImportContactStatus }> = ({
  status,
}) => {
  const styles: Record<ImportContactStatus, string> = {
    [ImportContactStatus.PENDING]:
      "bg-yellow-100 text-yellow-800",
    [ImportContactStatus.PROCESSING]:
      "bg-blue-100 text-blue-800",
    [ImportContactStatus.SUCCESS]:
      "bg-green-100 text-green-800",
    [ImportContactStatus.ERROR]:
      "bg-red-100 text-red-800",
    [ImportContactStatus.DUPLICATE]:
      "bg-orange-100 text-orange-800",
  };

  const labels: Record<ImportContactStatus, string> = {
    [ImportContactStatus.PENDING]: "Pending",
    [ImportContactStatus.PROCESSING]: "Processing",
    [ImportContactStatus.SUCCESS]: "Sent",
    [ImportContactStatus.ERROR]: "Error",
    [ImportContactStatus.DUPLICATE]: "Duplicate",
  };

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
};

const ContactsTable: React.FC<{
  contacts: ImportContact[];
  phase: ImportPhase;
  onRemove: (email: string) => void;
}> = ({ contacts, phase, onRemove }) => (
  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-800">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Email
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Role
          </th>
          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
            Status
          </th>
          {phase === ImportPhase.PREVIEW && (
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16" />
          )}
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
        {contacts.map((contact) => (
          <tr key={contact.email}>
            <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
              {contact.email}
              {contact.errorMessage && (
                <p className="text-xs text-red-500 mt-0.5">
                  {contact.errorMessage}
                </p>
              )}
            </td>
            <td className="px-4 py-2 text-sm text-gray-500 capitalize">
              {contact.role}
            </td>
            <td className="px-4 py-2 text-center">
              <StatusBadge status={contact.status} />
            </td>
            {phase === ImportPhase.PREVIEW && (
              <td className="px-4 py-2 text-center">
                <button
                  type="button"
                  onClick={() => onRemove(contact.email)}
                  className="text-red-400 hover:text-red-600 text-xs"
                >
                  ✕
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mb-4">
      <span className="text-sm text-gray-500">
        Page {currentPage} of {totalPages}
      </span>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const ModalActions: React.FC<{
  phase: ImportPhase;
  isProcessing: boolean;
  pendingCount: number;
  onClose: () => void;
  onStartImport: () => void;
  onReset: () => void;
}> = ({ phase, isProcessing, pendingCount, onClose, onStartImport, onReset }) => (
  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
    {phase === ImportPhase.UPLOAD && (
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
    )}

    {phase === ImportPhase.PREVIEW && (
      <>
        <Button variant="secondary" onClick={onReset}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onStartImport}
          disabled={pendingCount === 0}
        >
          Import ({pendingCount})
        </Button>
      </>
    )}

    {phase === ImportPhase.IMPORTING && (
      <Button variant="secondary" disabled={isProcessing}>
        Importing...
      </Button>
    )}

    {phase === ImportPhase.COMPLETED && (
      <Button variant="primary" onClick={onClose}>
        Done
      </Button>
    )}
  </div>
);

export default ContactImportModal;
